import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleAPI, visitorAPI, gatePassAPI, anprAPI, blacklistAPI } from '../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import StaffCheckIn from '../components/StaffCheckIn';
import { Html5QrcodeScanner } from 'html5-qrcode';

import { GuardHeader } from '../components/guard/GuardHeader';
import { ANPRLiveFeed } from '../components/guard/ANPRLiveFeed';
import { PlateVerification } from '../components/guard/PlateVerification';
import { VisitorRegistrationCard } from '../components/guard/VisitorRegistrationCard';
import { GuardCheckinTab } from '../components/guard/GuardCheckinTab';
import { VisitorFormModal, QRScannerModal } from '../components/guard/GuardModals';
import { getStoredUser, authFetch } from '../services/auth';
import { useCommunityConfig } from '../services/communityConfig';
import { useANPR, playAlert } from '../hooks/useANPR';
import { useGuardCheckins } from '../hooks/useGuardCheckins';

export default function GuardDashboard() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [user] = useState(getStoredUser());
  const { config } = useCommunityConfig();
  const [plateNumber, setPlateNumber] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [visitorForm, setVisitorForm] = useState({ name: '', phone: '', plate_number: '', villa_number: '', villa_numbers: [], purpose: '' });

  // QR Scanner State
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScanResult, setQrScanResult] = useState(null);
  const [qrValidating, setQrValidating] = useState(false);
  const qrScannerRef = useRef(null);

  // Villa Lookup State
  const [villaLookup, setVillaLookup] = useState('');
  const [villaVehicles, setVillaVehicles] = useState([]);
  const [villaLookupLoading, setVillaLookupLoading] = useState(false);
  const [villaDeliveryPasses, setVillaDeliveryPasses] = useState([]);
  const [deliveryEntryLoading, setDeliveryEntryLoading] = useState(null);
  const [blacklistInfo, setBlacklistInfo] = useState(null);
  const [manualApprovalLoading, setManualApprovalLoading] = useState(null);

  // Gate Pass Vehicle Entry State
  const [gatePassVehicleNumber, setGatePassVehicleNumber] = useState('');
  const [gatePassVehicleLoading, setGatePassVehicleLoading] = useState(false);
  const [gatePassVisitorPhoto, setGatePassVisitorPhoto] = useState(null);
  const [gatePassEntryCompleted, setGatePassEntryCompleted] = useState(false);

  // Custom hooks
  const anpr = useANPR();
  const guardCheckins = useGuardCheckins();

  // Timestamp formatters
  const formatIST = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let dateStr = timestamp;
      if (!dateStr.endsWith('Z') && !dateStr.includes('T')) { dateStr = dateStr.replace(' ', 'T') + 'Z'; }
      else if (!dateStr.endsWith('Z') && dateStr.includes('T')) { dateStr = dateStr + 'Z'; }
      return new Date(dateStr).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Date format error:', e); return 'N/A'; }
  };

  const formatISTTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      let dateStr = timestamp;
      if (!dateStr.endsWith('Z') && !dateStr.includes('T')) { dateStr = dateStr.replace(' ', 'T') + 'Z'; }
      else if (!dateStr.endsWith('Z') && dateStr.includes('T')) { dateStr = dateStr + 'Z'; }
      return new Date(dateStr).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Date format error:', e); return ''; }
  };

  // QR Scanner Effect
  const onQRCodeSuccess = useCallback(async (decodedText) => {
    setQrValidating(true);
    try {
      const qrData = JSON.parse(decodedText);
      if (qrData.type !== 'GATE_PASS') { setQrScanResult({ valid: false, message: 'Invalid QR code - not a gate pass' }); setQrValidating(false); return; }
      const response = await gatePassAPI.verify(qrData.code);
      if (response.data.valid) {
        setQrScanResult({ valid: true, message: 'ACCESS GRANTED', data: { ...qrData, ...response.data } });
        playAlert('GREEN');
        toast.success(`Access Granted for ${qrData.visitor}`);
      } else {
        setQrScanResult({ valid: false, message: response.data.message || 'Gate pass expired or invalid' });
        playAlert('RED');
        toast.error('Access Denied - Invalid or expired gate pass');
      }
    } catch (error) {
      try {
        const code = decodedText.trim();
        if (code.length === 4) {
          const response = await gatePassAPI.verify(code);
          if (response.data.valid) { setQrScanResult({ valid: true, message: 'ACCESS GRANTED', data: response.data }); playAlert('GREEN'); toast.success('Access Granted'); }
          else { setQrScanResult({ valid: false, message: 'Invalid or expired code' }); playAlert('RED'); }
        } else { setQrScanResult({ valid: false, message: 'Invalid QR code format' }); }
      } catch (e) { process.env.NODE_ENV !== 'production' && console.error('QR validation error:', e); setQrScanResult({ valid: false, message: 'Failed to validate QR code' }); }
    }
    setQrValidating(false);
    if (qrScannerRef.current) { qrScannerRef.current.clear().catch(console.error); qrScannerRef.current = null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQRCodeError = useCallback((error) => { console.debug('QR scan error:', error); }, []);

  // Blacklist check on each new ANPR reading
  useEffect(() => {
    const plate = anpr.latestReading?.plate;
    if (!plate || plate === 'NO PLATE') { setBlacklistInfo(null); return; }
    let cancelled = false;
    blacklistAPI.check(plate).then(({ data }) => {
      if (!cancelled) setBlacklistInfo(data?.is_blacklisted ? data : null);
    }).catch(() => { if (!cancelled) setBlacklistInfo(null); });
    return () => { cancelled = true; };
  }, [anpr.latestReading?.plate]);

  useEffect(() => {
    let timeoutId;
    if (showQRScanner && !qrScannerRef.current && !qrScanResult) {
      timeoutId = setTimeout(() => {
        const qrReaderElement = document.getElementById('qr-reader');
        if (qrReaderElement && !qrScannerRef.current) {
          try {
            const scanner = new Html5QrcodeScanner(
              "qr-reader",
              { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0, rememberLastUsedCamera: true, showTorchButtonIfSupported: true },
              false
            );
            scanner.render(onQRCodeSuccess, onQRCodeError);
            qrScannerRef.current = scanner;
          } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Error initializing QR scanner:', err); }
        }
      }, 500);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (qrScannerRef.current) { qrScannerRef.current.clear().catch(console.error); qrScannerRef.current = null; }
    };
  }, [showQRScanner, qrScanResult, onQRCodeSuccess, onQRCodeError]);

  const closeQRScanner = () => {
    if (qrScannerRef.current) { qrScannerRef.current.clear().catch(console.error); qrScannerRef.current = null; }
    setShowQRScanner(false);
    setQrScanResult(null);
  };

  const handleVerifyPlate = async () => {
    if (!plateNumber) { toast.error('Please enter a plate number'); return; }
    setScanning(true); setVerificationResult(null);
    try {
      const { data } = await vehicleAPI.verify(plateNumber);
      setVerificationResult(data);
      if (data.verified) toast.success('Access Granted - Vehicle Approved', { duration: 3000 });
      else toast.error('Access Denied - Vehicle Not Registered', { duration: 3000 });
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Plate verification error:', e); toast.error('Verification failed'); }
    finally { setTimeout(() => setScanning(false), 1000); }
  };

  const handleVerifyGatePass = async () => {
    if (!accessCode || accessCode.length !== 4) { toast.error('Please enter a 4-digit access code'); return; }
    setScanning(true); setVerificationResult(null); setGatePassVehicleNumber(''); setGatePassEntryCompleted(false); setGatePassVisitorPhoto(null);
    try {
      const { data } = await gatePassAPI.verify(accessCode);
      setVerificationResult(data);
      if (data.verified) toast.success('Pre-Approved Visitor - Access Granted', { duration: 3000 });
      else {
        toast.error(`${data.message}`, { duration: 3000 });
        setTimeout(() => { setVerificationResult((prev) => (prev && !prev.verified ? null : prev)); setAccessCode(''); }, 10000);
      }
    } catch (e) {
      process.env.NODE_ENV !== 'production' && console.error('Access code verification error:', e);
      toast.error('Verification failed');
      setTimeout(() => { setVerificationResult(null); setAccessCode(''); }, 10000);
    }
    finally { setTimeout(() => setScanning(false), 1000); }
  };

  const handleRecordGatePassVehicle = async () => {
    if (!gatePassVehicleNumber.trim()) { toast.error('Please enter the vehicle number plate'); return; }
    if (!verificationResult?.gate_pass?.id) return;
    setGatePassVehicleLoading(true);
    try {
      const { data } = await anprAPI.recordGatePassVehicle(verificationResult.gate_pass.id, gatePassVehicleNumber.trim().toUpperCase());
      toast.success(data.message || 'Vehicle number recorded');

      if (gatePassVisitorPhoto) {
        try {
          await anprAPI.uploadGatePassVisitorPhoto(verificationResult.gate_pass.id, gatePassVisitorPhoto);
          toast.success('Visitor photo uploaded');
        } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Photo upload error:', e); toast.warning('Vehicle recorded but photo upload failed'); }
      }

      anpr.setLatestReading(prev => ({
        ...prev,
        plate: gatePassVehicleNumber.trim().toUpperCase(),
        decision: 'GREEN',
        notes: `Gate pass entry | Visitor: ${verificationResult.gate_pass.visitor_name} | Villa ${verificationResult.gate_pass.villa_number}`
      }));
      setGatePassEntryCompleted(true);
      setGatePassVehicleNumber('');
      setGatePassVisitorPhoto(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record vehicle'); }
    finally { setGatePassVehicleLoading(false); }
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setShowCamera(false);
  }, [webcamRef]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCapturedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCreateVisitor = async () => {
    const villas = (visitorForm.villa_numbers && visitorForm.villa_numbers.length > 0)
      ? visitorForm.villa_numbers
      : (visitorForm.villa_number ? [visitorForm.villa_number] : []);
    if (!visitorForm.name || !visitorForm.phone || villas.length === 0) {
      toast.error('Please fill name, phone, and at least one villa');
      return;
    }
    try {
      const visitorData = {
        ...visitorForm,
        villa_numbers: villas,
        villa_number: villas[0], // back-compat: backend still accepts single
        photo: capturedImage || null,
      };
      const { data: visitor } = await visitorAPI.create(visitorData);
      await gatePassAPI.create({ visitor_id: visitor.id, visitor_name: visitor.name, villa_number: visitor.villa_number, valid_hours: 24 });
      if (villas.length > 1) {
        toast.success(`Visitor registered for ${villas.length} villas — all notified`);
      } else {
        toast.success('Visitor registered and gate pass generated!');
      }
      setShowVisitorForm(false);
      setVisitorForm({ name: '', phone: '', plate_number: '', villa_number: '', villa_numbers: [], purpose: '' });
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Visitor registration error:', e); toast.error('Failed to register visitor'); }
  };

  const handleVillaLookup = async () => {
    if (!villaLookup.trim()) { toast.error('Please enter a villa number'); return; }
    setVillaLookupLoading(true); setVillaVehicles([]); setVillaDeliveryPasses([]);
    const villa = villaLookup.trim().toUpperCase();
    try {
      const [vehResp, delResp] = await Promise.allSettled([
        vehicleAPI.getByVilla(villa),
        gatePassAPI.deliveryByVilla(villa),
      ]);
      const vehicles = vehResp.status === 'fulfilled' ? (vehResp.value.data || []) : [];
      const deliveries = delResp.status === 'fulfilled' ? (delResp.value.data || []) : [];
      setVillaVehicles(vehicles);
      setVillaDeliveryPasses(deliveries);
      if (vehicles.length === 0 && deliveries.length === 0) {
        toast.warning(`No vehicles or delivery pre-approvals for Villa ${villa}`);
      } else {
        toast.success(`Found ${vehicles.length} vehicle(s) and ${deliveries.length} delivery pre-approval(s) for Villa ${villa}`);
      }
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Villa lookup error:', e); toast.error('Failed to lookup villa'); setVillaVehicles([]); setVillaDeliveryPasses([]); }
    finally { setVillaLookupLoading(false); }
  };

  const handleDeliveryApprove = async (pass, plate = '') => {
    setDeliveryEntryLoading(pass.id);
    try {
      // Plate is optional. Guards can either type one in the inline field
      // (delivery rider's bike) or just hit Approve to log entry without it.
      const { data } = await gatePassAPI.guardApproveDelivery(pass.id, plate || '');
      const provider = pass.delivery_provider || pass.visitor_name;
      toast.success(
        plate
          ? `Entry approved for ${provider} (${plate})`
          : (data.message || `Entry approved for ${provider}`)
      );
      // refresh delivery list
      handleVillaLookup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setDeliveryEntryLoading(null);
    }
  };

  const handleManualApproval = async (vehicle) => {
    setManualApprovalLoading(vehicle.plate_number);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/anpr/manual-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          airtable_record_id: anpr.latestReading?.id || 'manual',
          vehicle_plate: vehicle.plate_number,
          owner_name: vehicle.owner_name,
          villa_number: vehicle.villa_number || villaLookup,
          note: 'Manual approval - Villa Lookup'
        })
      });
      if (response.ok) {
        toast.success(`Entry approved for ${vehicle.owner_name} (${vehicle.plate_number})`);
        anpr.setLatestReading(prev => ({
          ...prev, plate: vehicle.plate_number, decision: 'GREEN',
          notes: `Manual approval - Villa Lookup | ${vehicle.owner_name} | Villa ${vehicle.villa_number} | By: ${user.name}`
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record manual approval');
      }
    } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Manual approval error:', e); toast.error('Failed to process manual approval'); }
    finally { setManualApprovalLoading(null); }
  };

  const handleLogout = () => { localStorage.clear(); sessionStorage.clear(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gk-bg">
      <GuardHeader user={user} navigate={navigate} handleLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="gate" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-slate-200 p-1 rounded-lg">
            <TabsTrigger value="gate" className="data-[state=active]:bg-gk-navy-light data-[state=active]:text-white font-semibold">Gate Entry</TabsTrigger>
            <TabsTrigger value="guard-checkin" className="data-[state=active]:bg-gk-teal data-[state=active]:text-white font-semibold">Guard Check-in</TabsTrigger>
            <TabsTrigger value="staff" className="data-[state=active]:bg-gk-navy-light data-[state=active]:text-white font-semibold">Staff Check-in</TabsTrigger>
          </TabsList>

          <TabsContent value="gate">
            <div className={`grid grid-cols-1 ${config.anpr_enabled ? 'lg:grid-cols-2' : ''} gap-6`}>
              {config.anpr_enabled && (
                <ANPRLiveFeed
                  anprConnected={anpr.anprConnected}
                  anprConfigured={anpr.anprConfigured}
                  latestReading={anpr.latestReading}
                  recentReadings={anpr.recentReadings}
                  soundEnabled={anpr.soundEnabled}
                  setSoundEnabled={anpr.setSoundEnabled}
                  showSnapshot={anpr.showSnapshot}
                  setShowSnapshot={anpr.setShowSnapshot}
                  formatIST={formatIST}
                  formatISTTime={formatISTTime}
                  blacklistInfo={blacklistInfo}
                />
              )}
              <PlateVerification
                plateNumber={plateNumber}
                setPlateNumber={setPlateNumber}
                scanning={scanning}
                handleVerifyPlate={handleVerifyPlate}
                villaLookup={villaLookup}
                setVillaLookup={setVillaLookup}
                villaVehicles={villaVehicles}
                villaDeliveryPasses={villaDeliveryPasses}
                handleDeliveryApprove={handleDeliveryApprove}
                deliveryEntryLoading={deliveryEntryLoading}
                villaLookupLoading={villaLookupLoading}
                handleVillaLookup={handleVillaLookup}
                handleManualApproval={handleManualApproval}
                manualApprovalLoading={manualApprovalLoading}
                latestReading={anpr.latestReading}
                setShowQRScanner={setShowQRScanner}
                accessCode={accessCode}
                setAccessCode={setAccessCode}
                handleVerifyGatePass={handleVerifyGatePass}
                verificationResult={verificationResult}
                gatePassVehicleNumber={gatePassVehicleNumber}
                setGatePassVehicleNumber={setGatePassVehicleNumber}
                gatePassVehicleLoading={gatePassVehicleLoading}
                handleRecordGatePassVehicle={handleRecordGatePassVehicle}
                gatePassVisitorPhoto={gatePassVisitorPhoto}
                setGatePassVisitorPhoto={setGatePassVisitorPhoto}
                gatePassEntryCompleted={gatePassEntryCompleted}
              />
              <VisitorRegistrationCard setShowVisitorForm={setShowVisitorForm} />
            </div>
          </TabsContent>

          <TabsContent value="guard-checkin">
            <GuardCheckinTab
              guardSearchQuery={guardCheckins.guardSearchQuery}
              setGuardSearchQuery={guardCheckins.setGuardSearchQuery}
              guardSearchResults={guardCheckins.guardSearchResults}
              guardSearchLoading={guardCheckins.guardSearchLoading}
              handleGuardSearch={guardCheckins.handleGuardSearch}
              handleGuardCheckin={guardCheckins.handleGuardCheckin}
              guardCheckinLoading={guardCheckins.guardCheckinLoading}
              latestGuardCheckin={guardCheckins.latestGuardCheckin}
              guardCheckins={guardCheckins.guardCheckins}
              handleGuardCheckout={guardCheckins.handleGuardCheckout}
            />
          </TabsContent>

          <TabsContent value="staff">
            <StaffCheckIn />
          </TabsContent>
        </Tabs>
      </main>

      <VisitorFormModal
        showVisitorForm={showVisitorForm}
        setShowVisitorForm={setShowVisitorForm}
        visitorForm={visitorForm}
        setVisitorForm={setVisitorForm}
        handleCreateVisitor={handleCreateVisitor}
        showCamera={showCamera}
        setShowCamera={setShowCamera}
        webcamRef={webcamRef}
        capture={capture}
        capturedImage={capturedImage}
        handleImageUpload={handleImageUpload}
      />

      <QRScannerModal
        showQRScanner={showQRScanner}
        closeQRScanner={closeQRScanner}
        qrScanResult={qrScanResult}
        qrValidating={qrValidating}
        setQrScanResult={setQrScanResult}
        setShowQRScanner={setShowQRScanner}
        qrScannerRef={qrScannerRef}
        onQRCodeSuccess={onQRCodeSuccess}
        onQRCodeError={onQRCodeError}
        Html5QrcodeScanner={Html5QrcodeScanner}
      />
    </div>
  );
}
